class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const signal = this.abortController.signal;
    const base = routes.predictive_search_url;
    const q = encodeURIComponent(searchTerm);
    const term = searchTerm.trim().toLowerCase();

    const nameUrl = `${base}?q=${q}&section_id=predictive-search&options[prefix]=last&resources[fields]=title`;
    const skuUrl  = `${base}?q=${q}&section_id=predictive-search&options[prefix]=last&resources[type]=product&resources[fields]=variants.sku`;

    Promise.all([
      fetch(nameUrl, { signal }).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); }),
      fetch(skuUrl,  { signal }).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); }),
      PredictiveSearch.loadMpnCatalog(),
    ])
      .then(([nameText, skuText, catalog]) => {
        const parser = new DOMParser();
        const nameSection = parser.parseFromString(nameText, 'text/html').querySelector('#shopify-section-predictive-search');
        const skuSection  = parser.parseFromString(skuText,  'text/html').querySelector('#shopify-section-predictive-search');

        // 精确SKU过滤：从SKU搜索结果的data-skus属性逐字符比对
        const exactSkuItems = Array.from(
          skuSection.querySelectorAll('#predictive-search-results-products-list li')
        ).filter((li) => {
          const skus = (li.getAttribute('data-skus') || '').split('|').filter(Boolean);
          return skus.includes(term);
        });

        exactSkuItems.forEach((li) => {
          li.classList.add('predictive-search__item--exact-match');
          const heading = li.querySelector('.predictive-search__item-heading');
          if (heading) {
            const badge = document.createElement('span');
            badge.className = 'predictive-search__exact-badge';
            badge.textContent = 'SKU';
            heading.after(badge);
          }
          PredictiveSearch.updatePriceForVariant(li, term);
        });

        // 精确MPN过滤：从名称搜索结果的data-mpns属性逐字符比对
        // 这样可以覆盖全部产品，不受catalog数量限制
        const exactMpnItems = Array.from(
          nameSection.querySelectorAll('#predictive-search-results-products-list li')
        ).filter((li) => {
          const mpns = (li.getAttribute('data-mpns') || '').split('|').filter(Boolean);
          return mpns.includes(term);
        });

        exactMpnItems.forEach((li) => {
          li.classList.add('predictive-search__item--exact-match');
          const heading = li.querySelector('.predictive-search__item-heading');
          if (heading) {
            const badge = document.createElement('span');
            badge.className = 'predictive-search__exact-badge';
            badge.textContent = 'Part #';
            heading.after(badge);
          }
          PredictiveSearch.updatePriceForVariant(li, term);
        });

        // catalog兜底：覆盖不在名称搜索结果里的产品（如产品名称不含MPN关键词）
        const mpnMatches = catalog.filter((p) => p.mpns.some((m) => m && m.toLowerCase() === term));

        // 比较商品路径而不是完整链接，避免绝对/相对地址及variant参数导致同一商品重复。
        const getProductKey = (url) => {
          if (!url) return '';
          try {
            return new URL(url, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
          } catch (e) {
            return String(url).split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
          }
        };
        const getItemProductKey = (li) => getProductKey(li.querySelector('a')?.getAttribute('href'));

        const mpnItemKeys = new Set(exactMpnItems.map(getItemProductKey).filter(Boolean));
        const mpnCatalogKeys = new Set(mpnMatches.map((p) => getProductKey(p.url)).filter(Boolean));
        const skuKeys = new Set(exactSkuItems.map(getItemProductKey).filter(Boolean));

        const hasExact = exactSkuItems.length > 0 || exactMpnItems.length > 0 || mpnMatches.length > 0;

        let productList = nameSection.querySelector('#predictive-search-results-products-list');

        if (hasExact) {
          if (productList) {
            productList.innerHTML = '';
          } else {
            let wrapper = nameSection.querySelector('#predictive-search-results-groups-wrapper');
            if (!wrapper) {
              const resultsDiv = nameSection.querySelector('#predictive-search-results');
              if (resultsDiv) {
                wrapper = document.createElement('div');
                wrapper.id = 'predictive-search-results-groups-wrapper';
                wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
                resultsDiv.prepend(wrapper);
              }
            }
            if (wrapper) {
              const group = document.createElement('div');
              group.className = 'predictive-search__result-group';
              productList = document.createElement('ul');
              productList.id = 'predictive-search-results-products-list';
              productList.className = 'predictive-search__results-list list-unstyled';
              productList.setAttribute('role', 'group');
              group.appendChild(productList);
              wrapper.appendChild(group);
            }
          }
          if (productList) {
            // catalog兜底匹配（产品名不含MPN，走catalog找到）
            mpnMatches.forEach((p) => {
              if (!mpnItemKeys.has(getProductKey(p.url))) {
                productList.appendChild(PredictiveSearch.buildExactListItem(p, 'Part #', term));
              }
            });
            // 名称搜索结果里的精确MPN匹配
            exactMpnItems.forEach((li) => productList.appendChild(li));
            // 精确SKU匹配（排除已作为MPN显示的）
            exactSkuItems.forEach((li) => {
              const productKey = getItemProductKey(li);
              if (!mpnCatalogKeys.has(productKey) && !mpnItemKeys.has(productKey)) {
                productList.appendChild(li);
              }
            });
          }
        } else if (productList) {
          const allExactKeys = new Set([...mpnCatalogKeys, ...mpnItemKeys, ...skuKeys]);
          productList.querySelectorAll('li').forEach((li) => {
            if (allExactKeys.has(getItemProductKey(li))) li.remove();
          });
        }

        const resultsMarkup = nameSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((inst) => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// MPN catalog cache and loader
PredictiveSearch._mpnCatalogCache = null;
PredictiveSearch._mpnCatalogPromise = null;

PredictiveSearch.loadMpnCatalog = function () {
  if (PredictiveSearch._mpnCatalogCache) return Promise.resolve(PredictiveSearch._mpnCatalogCache);
  if (PredictiveSearch._mpnCatalogPromise) return PredictiveSearch._mpnCatalogPromise;
  PredictiveSearch._mpnCatalogPromise = PredictiveSearch._fetchMpnCatalogPage(1)
    .then(({ products, pages }) => {
      if (pages <= 1) {
        PredictiveSearch._mpnCatalogCache = products;
        return products;
      }
      const fetches = [];
      for (let p = 2; p <= Math.min(pages, 20); p++) {
        fetches.push(PredictiveSearch._fetchMpnCatalogPage(p).then((d) => d.products));
      }
      return Promise.all(fetches).then((rest) => {
        PredictiveSearch._mpnCatalogCache = products.concat(...rest);
        return PredictiveSearch._mpnCatalogCache;
      });
    })
    .catch(() => []);
  return PredictiveSearch._mpnCatalogPromise;
};

PredictiveSearch._fetchMpnCatalogPage = function (page) {
  return fetch(`/?sections=mpn-catalog&page=${page}`)
    .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then((json) => {
      const doc = new DOMParser().parseFromString(json['mpn-catalog'] || '', 'text/html');
      const raw = doc.getElementById('mpn-catalog-data')?.textContent || '{"products":[],"pages":1}';
      const data = JSON.parse(raw);
      return { products: (data.products || []).filter(Boolean), pages: data.pages || 1 };
    });
};

PredictiveSearch.updatePriceForVariant = function (li, needle) {
  try {
    const vd = JSON.parse(li.getAttribute('data-variant-data') || '[]');
    const v = vd.find((x) => x.sku === needle || (x.mpn && x.mpn === needle));
    if (!v) return;
    const priceDiv = li.querySelector('.price');
    if (priceDiv) {
      if (v.on_sale) {
        priceDiv.classList.add('price--on-sale');
        const saleSpan = priceDiv.querySelector('.price-item--sale');
        if (saleSpan) {
          const inner = saleSpan.querySelector('.discount_percentage-price');
          saleSpan.innerHTML = (inner ? inner.outerHTML : '') + ' ' + v.price + ' ';
        }
        const compareS = priceDiv.querySelector('.price__sale s.price-item--regular');
        if (compareS && v.compare) compareS.textContent = v.compare;
      } else {
        priceDiv.classList.remove('price--on-sale');
        const regularSpan = priceDiv.querySelector('.price__regular .price-item--regular');
        if (regularSpan) regularSpan.textContent = v.price;
      }
    }
    if (v.id) {
      const link = li.querySelector('a[href*="/products/"]');
      if (link) {
        try {
          const url = new URL(link.href);
          url.searchParams.set('variant', v.id);
          link.href = url.toString();
        } catch (e) {}
      }
    }
  } catch (e) {}
};

PredictiveSearch.buildExactListItem = function (entry, badgeText, needle) {
  const li = document.createElement('li');
  li.className = 'predictive-search__list-item predictive-search__item--exact-match';
  li.setAttribute('role', 'option');
  li.setAttribute('aria-selected', 'false');

  const a = document.createElement('a');
  a.href = entry.url;
  a.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
  a.setAttribute('tabindex', '-1');

  if (entry.image) {
    const img = document.createElement('img');
    img.className = 'predictive-search__image';
    img.src = entry.image;
    img.alt = entry.title;
    img.width = 50;
    img.height = 50;
    a.appendChild(img);
  }

  const content = document.createElement('div');
  content.className = 'predictive-search__item-content';

  const heading = document.createElement('p');
  heading.className = 'predictive-search__item-heading h5';
  heading.textContent = entry.title;
  content.appendChild(heading);

  const badge = document.createElement('span');
  badge.className = 'predictive-search__exact-badge';
  badge.textContent = badgeText;
  content.appendChild(badge);

  if (needle && entry.variants) {
    const v = entry.variants.find((x) => (x.mpn && x.mpn === needle) || (x.sku && x.sku === needle));
    if (v) {
      if (v.id) {
        try {
          const url = new URL(a.href, location.origin);
          url.searchParams.set('variant', v.id);
          a.href = url.toString();
        } catch (e) {}
      }
      const priceEl = document.createElement('div');
      if (v.on_sale) {
        priceEl.className = 'price price--on-sale';
        priceEl.innerHTML = '<div class="price__sale"><span class="price-item price-item--sale" style="display:block">' + v.price + '</span><span><s class="price-item price-item--regular">' + v.compare + '</s></span></div>';
      } else {
        priceEl.className = 'price';
        priceEl.innerHTML = '<div class="price__regular"><span class="price-item price-item--regular">' + v.price + '</span></div>';
      }
      content.appendChild(priceEl);
    }
  }

  a.appendChild(content);
  li.appendChild(a);
  return li;
};
